import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import Datepicker from 'react-datepicker';
import { withRouter } from 'react-router-dom';

import { getAveragePickleballRatingDisplayV2, getMatchDraw, isEventOfficialHsEvent } from '../../../utils/events';
import { sportTypeIds } from '../../../utils/eventConstants';
import { createTypeByIdDict, updateCategoryTypesName } from '../../../utils/formatting';
import { getDrawName } from '../../../utils/courtSchedular';
import { getTiebreak } from '../../../utils';
import Utr from '../../text/Utr';
import { MatchFormatTypes } from '../../../utils/postScoreConstants';
import { UNPUBLISHED_DRAWS_SCHEDULE_WARNING } from '../../../../constants/events';

const MatchSheet = (props) => {
    const { events, auth, coreUI, maxMatchDate, showUnpublishedDraws } = props;
    const [facility, setFacility] = useState('allFacilities');
    const eventStartDate = events.currentEvent?.data.eventSchedule.eventStartUtc;
    const eventEndDate = events.currentEvent?.data.eventSchedule.eventEndUtc;
    const eventTimeZone = events.currentEvent?.data.eventSchedule.timeZone.value;
    const isOfficialHSEvent = isEventOfficialHsEvent(events.currentEvent?.data);
    const sportTypeId = events.currentEvent?.data?.sportTypeId;
    const isTennisEvent = sportTypeId === sportTypeIds.TENNIS;
    const defaultDate = moment().isBefore(eventStartDate) || moment().isAfter(eventEndDate) ? eventStartDate : moment();
    const [date, setDate] = useState(moment(defaultDate).format());
    const [draw, setDraw] = useState('allDraws');
    const [allMatches, setAllMatches] = useState([]);
    const [matchesToShow, setMatchesToShow] = useState([]);
    const [showDrawsDropDown, setShowDrawsDropDown] = useState(false);

    const sortMatchesByMatchTime = matchesArray => {
        return matchesArray.sort((a, b) => {
            if (moment(a.date).isBefore(b.date)) {
                return -1;
            }
            if (moment(a.date).isAfter(b.date)) {
                return 1;
            }
            return 0;
        });
    };

    const sortMatchesWithLocation = matchesArray => {
        const matchesWithLocation = matchesArray.filter(match => {
            return events.currentEvent.matchSheet?.some(matchFromSheet => ((match.id === matchFromSheet.matchId) && matchFromSheet.locationId !== null));
        });

        const matchesWithoutLocation = matchesArray.filter(match => {
            return events.currentEvent.matchSheet?.some(matchFromSheet => {
                return match.id === matchFromSheet.matchId && matchFromSheet.locationId === null;
            });
        });

        const sortedMatchesWithLocationByTime = sortMatchesByMatchTime(matchesWithLocation);
        const sortedMatchesWithoutLocationByTime = sortMatchesByMatchTime(matchesWithoutLocation);

        return sortedMatchesWithLocationByTime.concat(sortedMatchesWithoutLocationByTime);
    };

    const getAllMatches = () => {
        const matches = events.currentEvent.data.draws?.reduce((acc, curr) => {
            if (curr.published || showUnpublishedDraws) {
                curr.rounds.forEach(round => {
                    round.matches.forEach(match => {
                        acc.push(match);
                    });
                });
                if (curr.childDraws?.length) {
                    curr.childDraws.forEach((childDraw) => {
                        childDraw.rounds.forEach(round => {
                            round.matches.forEach(match => {
                                acc.push(match);
                            });
                        });
                    });
                }
            }
            return acc;
        }, []);
        const allMatchesWithSchedule = matches.filter(match => match.date !== null);
        const filteredMatchesByDate = allMatchesWithSchedule.filter(match => moment(moment(date).format('YYYY-MM-DD')).isSame(moment.utc(match.date).tz(eventTimeZone).format('YYYY-MM-DD'), 'day'));
        const sortedMatchesOnMatchSheet = sortMatchesWithLocation(filteredMatchesByDate);
        setAllMatches(allMatchesWithSchedule);
        setMatchesToShow(sortedMatchesOnMatchSheet);
    };

    useEffect(() => {
        getAllMatches();
    }, [events.currentEvent]);

    useEffect(() => {
        const visibleDraws = (events.currentEvent?.data?.draws ?? []).filter(d => d.published || showUnpublishedDraws);
        if (visibleDraws.length > 0) {
            setShowDrawsDropDown(true);
        } else {
            setShowDrawsDropDown(false);
        }
    }, [events.currentEvent?.data?.draws, showUnpublishedDraws]);

    const hasUnpublishedMatches = useMemo(
        () => {
            const draws = events.currentEvent?.data?.draws;
            return !!draws && matchesToShow.some(m => getMatchDraw(m, draws)?.published === false);
        },
        [matchesToShow, events.currentEvent?.data?.draws],
    );

    const drawCategoryTypesDict = useMemo(() => {
        const { drawCategoryTypes = [] } = coreUI?.types?.tms?.draw || {};
        return updateCategoryTypesName(createTypeByIdDict(drawCategoryTypes));
    }, [coreUI?.types]);

    const printSchedule = () => {
        props.history.push(`/events/${events.currentEvent?.data?.id}/matchsheet`);
    };

    const getDrawNameForMatch = match => {
        const draws = events.currentEvent?.data?.draws;
        const foundDraw = getMatchDraw(match, draws);

        if (foundDraw?.parentDrawName) {
            const parentDraw = draws?.find(d => d.childDraws?.some(cd => cd.id === foundDraw.id));
            return `${foundDraw.parentDrawName} (${getDrawName(foundDraw, parentDraw, drawCategoryTypesDict, true)})`;
        }
        return foundDraw?.name;
    };

    const filterMatchesByDate = (matchesArray, selectedDate) => {
        const filteredMatchesByDate = matchesArray.filter(match => moment(moment(selectedDate).format('YYYY-MM-DD')).isSame(moment.utc(match.date).tz(eventTimeZone).format('YYYY-MM-DD'), 'day'));
        return filteredMatchesByDate;
    };

    const filterMatchesByFacility = (matchesArray, facilityName) => {
        return matchesArray.filter(match => {
            const foundMatchObject = events.currentEvent.matchSheet?.find(matchFromSheet => matchFromSheet.matchId === match.id);
            if (foundMatchObject) {
                const locationObject = events.currentEvent.locations?.find(eventLocation => eventLocation.id === foundMatchObject?.locationId);
                const facilityObject = locationObject?.facilities.find(locationFacility => {
                    return locationFacility.id === foundMatchObject?.facilityId;
                });
                return facilityObject?.name === facilityName;
            }
            return null;
        });
    };

    const filterMatchesByDraw = (matchesArray, drawName) => {
        return matchesArray.filter(match => {
            const nameOfDraw = getDrawNameForMatch(match);
            return nameOfDraw === drawName;
        });
    };

    const matchFilter = (selectedDate, selectedFacility, selectedDraw) => {
        const sortedMatchesOnSheet = sortMatchesWithLocation(allMatches);
        let filteredMatchSheet = filterMatchesByDate(sortedMatchesOnSheet, selectedDate);
        if (selectedFacility !== 'allFacilities') {
            filteredMatchSheet = filterMatchesByFacility(filteredMatchSheet, selectedFacility);
        }
        if (selectedDraw !== 'allDraws') {
            filteredMatchSheet = filterMatchesByDraw(filteredMatchSheet, selectedDraw);
        }
        return setMatchesToShow(filteredMatchSheet);
    };

    const handleDateChange = (selectedDate) => {
        const formattedDate = (moment(selectedDate).format());
        setDate(formattedDate);
        return matchFilter(formattedDate, facility, draw);
    };

    const handleFacilityChange = (e) => {
        setFacility(e.target.value);
        return matchFilter(date, e.target.value, draw);
    };

    const handleDrawChange = (e) => {
        setDraw(e.target.value);
        return matchFilter(date, facility, e.target.value);
    };

    const renderRating = (player, ratingType, playerId, column) => {
        if (!player) {
            return null;
        }
        return (
            <span className={`${column === 'left' ? 'pr4' : ''} ${column === 'right' ? 'pl4' : ''}`}>
                <Utr
                    player={player}
                    sportTypeId={sportTypeId}
                    force={{ ratingType }}
                    data-testid={`match-sheet.player-${playerId}.rating`}
                />
            </span>
        );
    };

    const notRated = 'UR';
    const getPlayerDetails = (match, column) => {
        if (match.position1?.type.value === 'player') {
            if (column === 'left' && !!match.position1?.id) {
                const player1 = events.currentEvent.playersDict[match.position1.id];
                return (
                    <>
                        <div className="col-sm-12 p-0 justify-content-md-end"> {renderRating(player1, MatchFormatTypes.SINGLES, match.position1.id, column)} {player1.displayName} </div>
                        {isOfficialHSEvent && (
                            <div className="col-sm-12 p-0 justify-content-md-end"> {player1.highSchool} </div>
                        )}
                    </>
                );
            }
        }

        if (match.position2?.type.value === 'player') {
            if (column === 'right' && !!match.position2?.id) {
                const player2 = events.currentEvent.playersDict[match.position2.id];
                return (
                    <>
                        <div className="col-sm-12 p-0"> {player2.displayName} {renderRating(player2, MatchFormatTypes.SINGLES, match.position2.id, column)} </div>
                        {isOfficialHSEvent && (
                            <div className="col-sm-12 p-0"> {player2.highSchool} </div>
                        )}
                    </>
                );
            }
        }

        if (match.position1?.type.value === 'team') {
            if (column === 'left' && !!match.position1?.id) {
                const team1 = events.currentEvent.teamsDict[match.position1.id];
                const team1Player1 = events.currentEvent.playersDict[team1.players[0]];
                const team1Player2 = events.currentEvent.playersDict[team1.players[1]];
                const team1Rating = isTennisEvent
                    ? (team1.averageDoublesUtr > 0 && team1.averageDoublesUtr.toFixed(2)) || notRated
                    : getAveragePickleballRatingDisplayV2(team1Player1, team1Player2);

                return (
                    <>
                        <div className="col-sm-12 p-0 justify-content-md-end"> {team1Rating} {team1.displayName} </div>
                        {isOfficialHSEvent && (
                            <div className="col-sm-12 p-0 justify-content-md-end"> {team1Player1.highSchool} {team1Player2.highSchool && team1Player2.highSchool !== team1Player1.highSchool ? `/ ${team1Player2.highSchool}` : ''}</div>
                        )}
                    </>
                );
            }
        }

        if (match.position2?.type.value === 'team') {
            if (column === 'right' && !!match.position2?.id) {
                const team2 = events.currentEvent.teamsDict[match.position2.id];
                const team2Player1 = events.currentEvent.playersDict[team2.players[0]];
                const team2Player2 = events.currentEvent.playersDict[team2.players[1]];
                const team2Rating = isTennisEvent
                    ? (team2.averageDoublesUtr > 0 && team2.averageDoublesUtr.toFixed(2)) || notRated
                    : getAveragePickleballRatingDisplayV2(team2Player1, team2Player2);

                return (
                    <>
                        <div className="col-sm-12 p-0"> {team2.displayName} {team2Rating} </div>
                        {isOfficialHSEvent && (
                            <div className="col-sm-12 p-0">  {team2Player1.highSchool} {team2Player2.highSchool && team2Player2.highSchool !== team2Player1.highSchool ? `/ ${team2Player2.highSchool}` : ''} </div>
                        )}
                    </>
                );
            }
        }

        return '';
    };

    const getScores = (match) => {
        const walkOverValues = ['Walkover', 'Defaulted', 'Withdrew', 'walkover', 'defaulted', 'withdrew', 'Abandoned Match', 'Double Walkover', 'abandoned match', 'double walkover'];
        const score = match.result?.score;
        const drawType = getMatchDraw(match, events.currentEvent.data.draws)?.drawFormatType.value;

        if (score) {
            const scoreArray = Object.values(score);
            let didPositionOneWin = false;
            if (drawType === 'doubles' && !!match.position1?.id) {
                const playersInPositionOne = events.currentEvent.teamsDict[match.position1.id].players;
                if (playersInPositionOne.includes(match.result?.players.winner1.id)) {
                    didPositionOneWin = true;
                }
            }
            if (drawType === 'singles' && !!match.position1?.id) {
                const playerInPositionOneId = events.currentEvent.playersDict[match.position1.id].id;
                if (playerInPositionOneId.toString() === match.result?.players.winner1.id) {
                    didPositionOneWin = true;
                }
            }
            if (scoreArray.length > 0) {
                return (
                    <>
                        {scoreArray.map(scoreItem => {
                            if (didPositionOneWin) {
                                return `${scoreItem.winner}-${scoreItem.loser}${getTiebreak(scoreItem)} `;
                            }
                            return `${scoreItem.loser}-${scoreItem.winner}${getTiebreak(scoreItem)} `;
                        })}
                    </>
                );
            }
            if ((walkOverValues.includes(match.result.completionType) || (match.result.completionType === 'winnerdidnotstart'))) {
                return (
                    <span className="outcome-font">{match.result.completionType || 'Walkover'}</span>
                );
            }
        }
        return '';
    };

    const getMatchDetails = matchObject => {
        let facilityAbbreviation = '';
        let courtName = '';
        if (events.currentEvent.matchSheet?.length && events.currentEvent.locations?.length) {
            const foundMatchObject = events.currentEvent.matchSheet?.find(match => match.matchId === matchObject.id);

            if (foundMatchObject) {
                const locationObject = events.currentEvent.locations?.find(eventLocation => eventLocation.id === foundMatchObject?.locationId);

                const facilityObject = locationObject?.facilities.find(locationFacility => {
                    return locationFacility.id === foundMatchObject?.facilityId;
                });
                facilityAbbreviation = facilityObject?.abbreviation ? `${facilityObject.abbreviation}` : '';

                const courtObject = facilityObject?.courts.find(court => {
                    return court.id === foundMatchObject?.courtId;
                });
                courtName = courtObject?.name ? `${courtObject.name}` : '';
            }
        }
        return `${facilityAbbreviation} ${courtName}`;
    };

    return (
        <>
            <div className="row mt16">
                <div className="col-md-8 d-flex">
                    <div className="form-inline">
                        <div className={`form-group form-group-inline`}>
                            <div className="input-wrapper mr8">
                                <Datepicker
                                    className="form-control form-control-sm"
                                    selected={moment(date)}
                                    onChange={handleDateChange}
                                    maxDate={maxMatchDate}
                                />
                            </div>
                            <div className="input-wrapper mr8">
                                <select
                                    className="form-control form-control-sm"
                                    value={facility}
                                    onChange={handleFacilityChange}
                                    data-testid="match-sheet.toolbar.facility-select"
                                >
                                    <option value={'allFacilities'}>All Facilities</option>
                                    {events.currentEvent.locations?.map(eventLocation => eventLocation.facilities.map(locationFacility => {
                                        if (events.currentEvent.matchSheet.some(matchFromSheet => matchFromSheet.facilityId === locationFacility.id)) {
                                            return <option key={locationFacility.id} value={locationFacility.name}>{locationFacility.name}</option>;
                                        }
                                        return null;
                                    }))}
                                </select>
                            </div>
                            {showDrawsDropDown && (
                                <div className="input-wrapper mr8">
                                    <select
                                        className="form-control form-control-sm"
                                        value={draw}
                                        onChange={handleDrawChange}
                                        data-testid="match-sheet.toolbar.draw-select"
                                    >
                                        {events.currentEvent.data.draws && events.currentEvent.data.draws.length && (
                                            <>
                                                <option value={'allDraws'}>All Draws</option>
                                                {events.currentEvent.data.draws.map(eventDraw => {
                                                    if (eventDraw.published || showUnpublishedDraws) {
                                                        return <option key={eventDraw.id} value={eventDraw.name}>{eventDraw.name}</option>;
                                                    }
                                                    return null;
                                                })}
                                            </>
                                        )}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-md-4 d-none d-md-flex aic jce">
                    {auth?.user && !props.hidePrintButton && (
                        <button
                            type="button"
                            className="btn btn-small btn-default float-right"
                            onClick={printSchedule}
                            data-testid="match-sheet.toolbar.print-btn"
                        >
                            <i className="fas fa-print" /> Print
                        </button>
                    )}
                </div>
            </div>
            {showUnpublishedDraws && hasUnpublishedMatches && (
                <div
                    className="alert alert-warning mt16"
                    role="alert"
                    data-testid="match-sheet.preview.unpublished-warning"
                >
                    {UNPUBLISHED_DRAWS_SCHEDULE_WARNING}
                </div>
            )}
            {matchesToShow.length > 0 && (
                <div className="d-none d-md-flex text-small text-medium">
                    <div className="flex-grow p8 mt16 mb-2">
                        <div className="row w-100">
                            <div className={`col-md-2`} data-testid="match-sheet.col-header.match-time">
                                Match Time
                            </div>
                            <div className={`col-md-2`} data-testid="match-sheet.col-header.location">
                                Location
                            </div>
                            <div className={`col-md-2`} data-testid="match-sheet.col-header.draw">
                                Draw
                            </div>
                            <div className={`col-md-2 text-right`} data-testid="match-sheet.col-header.player-team-a">
                                Player/Team A
                            </div>
                            <div className={`col-md-2 text-left`} data-testid="match-sheet.col-header.player-team-b">
                                Player/Team B
                            </div>
                            <div className={`col-md-2`} data-testid="match-sheet.col-header.match-result">
                                Match Result
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {matchesToShow.length > 0 && (
                matchesToShow.map(match => {
                    return (
                        <div key={match.id} className={`${props.hidePrintButton ? 'border-bottom border-dark' : ''} utr-card p8 bg-white`}>
                            <div className="row w-100">
                                <div className={`col-md-2`}>
                                    {moment.utc(`${match.date}`).tz(eventTimeZone).format('h:mm A z')}
                                </div>
                                <div className={`col-md-2`}>
                                    {getMatchDetails(match)}
                                </div>
                                <div className={`col-md-2`}>
                                    {getDrawNameForMatch(match)}
                                </div>
                                <div className={`col-md-2 d-block text-right justify-content-md-end`}>
                                    {getPlayerDetails(match, 'left')}
                                </div>
                                <div className={`col-md-2 d-block`}>
                                    {getPlayerDetails(match, 'right')}
                                </div>
                                <div className={`col-md-2`}>
                                    {getScores(match)}
                                </div>
                            </div>
                        </div>
                    );
                })
            )}

        </>
    );
};

MatchSheet.propTypes = {
    events: PropTypes.shape({
        currentEvent: PropTypes.shape({
            data: PropTypes.object,
            matchSheet: PropTypes.array,
            locations: PropTypes.array,
            playersDict: PropTypes.object,
            teamsDict: PropTypes.object,
        }),
    }).isRequired,
    auth: PropTypes.shape({
        user: PropTypes.object,
    }),
    coreUI: PropTypes.shape({
        types: PropTypes.object,
    }),
    maxMatchDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    showUnpublishedDraws: PropTypes.bool,
    hidePrintButton: PropTypes.bool,
    history: PropTypes.shape({
        push: PropTypes.func,
    }).isRequired,
};

MatchSheet.defaultProps = {
    auth: null,
    coreUI: null,
    maxMatchDate: null,
    showUnpublishedDraws: false,
    hidePrintButton: false,
};

function mapStateToProps({ auth, events, coreUI }) {
    return { auth, events, coreUI };
}

export default withRouter(connect(mapStateToProps, null)(MatchSheet));
